// Sign-in state for the doctor portal.
//
// No token is ever written to browser storage. The refresh token lives only in
// an HttpOnly cookie the API sets (scoped to /v1/auth, SameSite=Strict), which
// no script on the page can read; the 15-minute access token lives only in
// this module's memory. A page reload therefore starts with no access token
// and restores the session with one cookie-backed refresh (restoreSession).
//
// Every auth call sends `X-Kyros-Token-Transport: cookie` so the API uses the
// cookie instead of returning the refresh token in the body, and
// `credentials: 'include'` so the cookie travels when the API is on its own
// subdomain (api.kyrosclinic.com).

// Empty (relative) by default — same rule as ./api: calls resolve against the
// page's origin and ride Vite's dev proxy locally.
export const API_BASE = (import.meta.env['VITE_API_BASE_URL'] ?? '').replace(/\/$/, '');

const AUTH_HEADERS = {
  'Content-Type': 'application/json',
  'X-Kyros-Token-Transport': 'cookie',
};

// Keys an older build kept tokens under. Removed on load so nothing lingers.
for (const legacyKey of ['kyros_dr_access', 'kyros_dr_refresh']) {
  try { localStorage.removeItem(legacyKey); } catch { /* storage blocked */ }
}

let accessToken: string | null = null;

function decode(token: string): Record<string, unknown> | null {
  try {
    return JSON.parse(atob(token.split('.')[1]!)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

async function authPost(path: string, body: unknown): Promise<Response> {
  return fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: AUTH_HEADERS,
    credentials: 'include',
    body: JSON.stringify(body),
  });
}

async function errorDetail(resp: Response, fallback: string): Promise<string> {
  const body = await resp.json().catch(() => ({})) as Record<string, unknown>;
  return (body['detail'] as string | undefined) ?? fallback;
}

/** Accept a fresh access token, but only a doctor's. */
function acceptToken(token: string): void {
  if (decode(token)?.['role'] !== 'doctor') {
    accessToken = null;
    throw new Error('doctor_role_required');
  }
  accessToken = token;
}

export type LoginOutcome =
  | { status: 'signed_in' }
  | { status: 'mfa_required'; challengeToken: string };

export async function login(emailOrPhone: string, password: string): Promise<LoginOutcome> {
  const resp = await authPost('/v1/auth/login', { email_or_phone: emailOrPhone, password });
  if (!resp.ok) throw new Error(await errorDetail(resp, 'Login failed'));

  const data = await resp.json() as { access_token?: string; mfa_required?: boolean; challenge_token?: string };
  if (data.mfa_required && data.challenge_token) {
    return { status: 'mfa_required', challengeToken: data.challenge_token };
  }
  acceptToken(data.access_token ?? '');
  return { status: 'signed_in' };
}

/** Second step of sign-in when MFA is on: an authenticator or recovery code. */
export async function verifyMfa(challengeToken: string, code: string): Promise<void> {
  const resp = await authPost('/v1/auth/mfa/verify', {
    challenge_token: challengeToken,
    code: code.replace(/\s+/g, ''),
  });
  if (!resp.ok) throw new Error(await errorDetail(resp, 'mfa_invalid_code'));
  const data = await resp.json() as { access_token: string };
  acceptToken(data.access_token);
}

// The cookie's refresh token rotates on every use, and presenting an already
// rotated one is treated as theft (the whole session is revoked). So every
// caller — page load, a 401 retry, React dev mode running an effect twice —
// shares one in-flight refresh instead of racing its own.
let refreshInFlight: Promise<boolean> | null = null;

/** Exchange the refresh cookie for a new access token. Used on page load and
 *  after a 401. Returns false when there is no valid session to restore. */
export function restoreSession(): Promise<boolean> {
  refreshInFlight ??= refreshOnce().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

async function refreshOnce(): Promise<boolean> {
  try {
    const resp = await authPost('/v1/auth/refresh', {});
    if (!resp.ok) {
      accessToken = null;
      return false;
    }
    const data = await resp.json() as { access_token: string };
    acceptToken(data.access_token);
    return true;
  } catch {
    accessToken = null;
    return false;
  }
}

export async function requestPasswordReset(identifier: string): Promise<void> {
  // Response is deliberately generic (no account enumeration); we ignore the body.
  await fetch(`${API_BASE}/v1/auth/password-reset/request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ identifier }),
  });
}

export async function confirmPasswordReset(
  identifier: string,
  otp: string,
  newPassword: string,
): Promise<void> {
  const resp = await fetch(`${API_BASE}/v1/auth/password-reset/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ identifier, otp, new_password: newPassword }),
  });
  if (!resp.ok) throw new Error(await errorDetail(resp, 'reset_failed'));
}

/** Sign out: the API revokes the session and clears the cookie. The in-memory
 *  token is dropped even if the network call fails. */
export async function logout(): Promise<void> {
  accessToken = null;
  try {
    await authPost('/v1/auth/logout', {});
  } catch {
    /* offline — the cookie expires on its own and the session idles out */
  }
}

/** Drop the in-memory token without a network call (after a failed refresh). */
export function forgetSession(): void {
  accessToken = null;
}

export function getToken(): string | null {
  return accessToken;
}

/** Decodes the logged-in doctor's user id (`sub`) from the access token, for
 *  keying per-doctor local preferences (e.g. remembered camera/mic) so a
 *  shared workstation doesn't leak one doctor's device choice to another. */
export function getDoctorId(): string | null {
  const sub = accessToken ? decode(accessToken)?.['sub'] : null;
  return typeof sub === 'string' ? sub : null;
}

export function isAuthenticated(): boolean {
  return accessToken !== null && decode(accessToken)?.['role'] === 'doctor';
}
